import React, { useEffect, useState } from 'react';
import { apiGet, apiPatch } from '../api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Loader2, Eye, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../lib/apiError';

const STATUS_BADGE = {
  pending: { label: 'Pending', cls: 'bg-amber-500 text-white' },
  submitted_to_comreg: { label: 'At ComReg', cls: 'bg-sky-600 text-white' },
  active: { label: 'Active', cls: 'bg-emerald-500 text-white' },
  rejected: { label: 'Rejected', cls: 'bg-red-600 text-white' },
};

const ORG_TYPE_LABEL = {
  company: 'Company',
  health_professional: 'Health Professional',
  partnership_sole_trader: 'Partnership / Sole Trader',
};

// Allowed forward transitions per status — keeps the admin UI honest.
const NEXT_STATUSES = {
  pending: ['submitted_to_comreg', 'rejected'],
  submitted_to_comreg: ['active', 'rejected'],
  active: ['rejected'],
  rejected: ['pending'],
};

const STATUS_BUTTON_LABEL = {
  submitted_to_comreg: 'Mark Submitted to ComReg',
  active: 'Activate',
  rejected: 'Reject',
  pending: 'Reopen as Pending',
};

export const AdminSenderIdRequests = () => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [pending, setPending] = useState(null); // status currently being PATCHed

  const load = async () => {
    setLoading(true);
    try {
      const data = await apiGet('/api/admin/sender-id-requests');
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e, 'Could not load sender-ID requests'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openDetail = (req) => {
    setSelected(req);
    setAdminNotes(req.admin_notes || '');
  };

  const advance = async (status) => {
    if (!selected) return;
    setPending(status);
    try {
      const data = await apiPatch('/api/admin/sender-id-requests/{request_id}', {
        params: { request_id: selected.id },
        body: { status, admin_notes: adminNotes || null },
      });
      toast.success(`Request marked ${STATUS_BADGE[status].label}.`);
      setSelected(data);
      setItems((prev) => prev.map((i) => (i.id === data.id ? data : i)));
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to update status'));
    } finally {
      setPending(null);
    }
  };

  if (loading) {
    return (
      <div className="py-12 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-sender-id-requests">
      <Card>
        <CardHeader>
          <CardTitle>Sender ID Requests</CardTitle>
          <CardDescription>
            Review studio applications for personalised SMS sender IDs and advance their status through the
            pending → submitted to ComReg → active lifecycle.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {items.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground text-sm">
              No sender-ID requests yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-muted-foreground border-b">
                  <tr>
                    <th className="px-4 py-3">Studio</th>
                    <th className="px-4 py-3">Sender ID</th>
                    <th className="px-4 py-3">Contact</th>
                    <th className="px-4 py-3">Submitted</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((req) => {
                    const meta = STATUS_BADGE[req.status] || STATUS_BADGE.pending;
                    return (
                      <tr key={req.id} className="border-b hover:bg-muted/40 transition-colors">
                        <td className="px-4 py-3 font-medium">{req.studio_name || req.studio_id}</td>
                        <td className="px-4 py-3 font-mono">{req.requested_sender_id}</td>
                        <td className="px-4 py-3">
                          {req.contact_first_name} {req.contact_last_name}
                          <br />
                          <span className="text-xs text-muted-foreground">{req.contact_email}</span>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {new Date(req.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={meta.cls}>{meta.label}</Badge>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDetail(req)}
                            data-testid={`sender-id-view-${req.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Review
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  Sender ID — <span className="font-mono">{selected.requested_sender_id}</span>
                  <Badge className={STATUS_BADGE[selected.status].cls}>
                    {STATUS_BADGE[selected.status].label}
                  </Badge>
                </DialogTitle>
                <DialogDescription>
                  Submitted by {selected.studio_name || selected.studio_id} on{' '}
                  {new Date(selected.created_at).toLocaleString()}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6 py-2">
                <DetailGrid
                  title="Organisation"
                  rows={[
                    ['Type', ORG_TYPE_LABEL[selected.organisation_type]],
                    ['Phone', selected.organisation_phone],
                    ['Email', selected.organisation_email],
                    ['Tax Reg #', selected.tax_registration_number],
                    ['CRO #', selected.cro_number],
                  ]}
                />
                <DetailGrid
                  title="Registered Address"
                  rows={[
                    ['Address', selected.registered_address],
                    ['City', selected.registered_city],
                    ['County', selected.registered_county],
                    ['EirCode', selected.registered_eircode],
                  ]}
                />
                <DetailGrid
                  title="Business Address"
                  rows={[
                    ['Address', selected.business_address],
                    ['City', selected.business_city],
                    ['County', selected.business_county],
                    ['EirCode', selected.business_eircode],
                  ]}
                />
                <DetailGrid
                  title="Primary Contact"
                  rows={[
                    ['Name', `${selected.contact_first_name} ${selected.contact_last_name}`],
                    ['Phone', selected.contact_phone],
                    ['Email', selected.contact_email],
                  ]}
                />

                <div>
                  <h4 className="font-semibold mb-2">Admin notes</h4>
                  <Textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    rows={3}
                    placeholder="Internal notes (visible to the studio)…"
                    data-testid="admin-notes-textarea"
                  />
                </div>
              </div>

              <DialogFooter className="gap-2 flex-wrap">
                {(NEXT_STATUSES[selected.status] || []).map((s) => {
                  const isActivate = s === 'active';
                  const isReject = s === 'rejected';
                  return (
                    <Button
                      key={s}
                      onClick={() => advance(s)}
                      disabled={pending === s}
                      variant={isReject ? 'destructive' : isActivate ? 'default' : 'secondary'}
                      data-testid={`sender-id-advance-${s}`}
                    >
                      {pending === s ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <ChevronRight className="w-4 h-4 mr-1" />
                      )}
                      {STATUS_BUTTON_LABEL[s]}
                    </Button>
                  );
                })}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const DetailGrid = ({ title, rows }) => (
  <div>
    <h4 className="font-semibold mb-2">{title}</h4>
    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
      {rows.map(([k, v]) => (
        <div key={k} className="flex flex-col">
          <span className="text-xs text-muted-foreground">{k}</span>
          <span>{v || '—'}</span>
        </div>
      ))}
    </div>
  </div>
);

export default AdminSenderIdRequests;
